import { useCallback, useState } from "react";
import { useToast } from "@/hooks/useToast";
import { resourcesApi } from "@/services/api";
import type { ProgramResource } from "@/services/api";
import { resourcesQueryKey } from "@/hooks/useProgramResources";
import { useQueryClient } from "@tanstack/react-query";
import { validateResourceFile } from "@/utils/documentValidation";

/**
 * Files staged for the next message.
 *
 * Shared by all three message composers (group, PAB 1:1, Program 1:1) so the
 * upload rules live in one place — client-side validation, sequential uploads,
 * and surfacing the server's rejection reason.
 *
 * Files upload the MOMENT they are picked, not on send, so an attachment is
 * already a stored resource by the time the message goes out. That is why this
 * returns `ProgramResource` objects rather than raw `File`s: it reuses the
 * resource pipeline wholesale (validation, magic-byte checks, versioning,
 * presigned downloads) instead of the send endpoint growing a second upload
 * path. It also means a rejected file is reported immediately rather than
 * taking a typed-out message down with it.
 *
 * `programId` is the conversation's programme — a PAB session id and an Expert
 * Program id are both `programs.id`.
 */
export const useMessageAttachments = (programId: number | null | undefined) => {
  const queryClient = useQueryClient();
  const { showErrorToast } = useToast();
  const [pending, setPending] = useState<ProgramResource[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const attach = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      if (!programId) {
        showErrorToast("Pick a conversation before attaching a file.", "Attach failed");
        return;
      }
      setIsUploading(true);
      try {
        // Sequential, not parallel: writes are rate-limited at 25/minute per
        // IP, so a batch of ten firing at once would trip the limit and fail
        // most of the batch.
        for (const file of Array.from(files)) {
          const problem = validateResourceFile(file);
          if (problem) {
            // Report and skip — one bad file shouldn't abandon the rest.
            showErrorToast(problem, "File not accepted");
            continue;
          }
          try {
            const created = await resourcesApi.upload(programId, file, { source: "MESSAGE" });
            setPending((prev) => [...prev, created]);
            // Deliberately NOT refreshing the Resources tab here. An uploaded
            // file is still only STAGED: the server can't tell a group
            // attachment from a private 1:1 one until the message linking it
            // exists, so it withholds every unsent MESSAGE file from the tab.
            // Callers refresh after a successful group send instead.
          } catch (err: unknown) {
            const detail =
              (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
              "Could not upload that file.";
            showErrorToast(detail, "Attach failed");
          }
        }
      } finally {
        setIsUploading(false);
      }
    },
    [programId, showErrorToast],
  );

  /** Drops a file from the OUTGOING message only. The file itself stays
   *  uploaded — it already belongs to the programme. */
  const remove = useCallback((id: number) => {
    setPending((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clear = useCallback(() => setPending([]), []);

  /** Pull the Resources tab back in step after a GROUP message carrying
   *  attachments is sent — that send is what promotes the staged files into
   *  programme resources. 1:1 sends must NOT call this: those files never
   *  belong to the shared tab. */
  const refreshProgramResources = useCallback(() => {
    if (!programId) return;
    void queryClient.invalidateQueries({ queryKey: resourcesQueryKey(programId) });
  }, [programId, queryClient]);

  return {
    pending,
    isUploading,
    attach,
    remove,
    clear,
    refreshProgramResources,
    /** True once a conversation (and therefore a programme) is known. */
    canAttach: !!programId,
  };
};
